import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, Observable, pluck, Subject, Subscription } from 'rxjs';
import { IDrawerConfig } from './drawer.interface';
import { allowClickHandlerList } from './constants';
import { NavigationStart, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DialogData } from '@app/shared/interfaces/dialog.interface';
import { BasicButtonColors, BasicButtonModes } from '@app/shared/enums/buttons';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { FormGroup } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class DrawerService implements OnDestroy {
  public toggle$: Subject<{ open: boolean; data?: any }>;
  public drawerConfig$: BehaviorSubject<IDrawerConfig>;
  public contentChange$: Subject<any>;
  public scrollEnds$: Subject<void>;
  public toggleDisabledButton$: Subject<boolean>;
  public afterClose$: Subject<any>;
  public loading$: Subject<boolean>;
  public data: any;
  public formChangedValue = false;
  private targetRoute = '';
  private isOpen: boolean;
  private dialogSubscription = new Subscription();

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private translateService: TranslateService,
  ) {
    this.toggleDisabledButton$ = new Subject<boolean>();
    this.scrollEnds$ = new Subject<void>();
    this.toggle$ = new Subject<{ open: boolean; data?: any }>();
    this.drawerConfig$ = new BehaviorSubject<IDrawerConfig>({ title: '' });
    this.contentChange$ = new Subject<any>();
    this.afterClose$ = new Subject<any>();
    this.loading$ = new Subject<boolean>();
    this.isOpen = false;
    this.handleNavigationEvent();
  }

  public open(component: ComponentType<unknown>, data?: any): void {
    this.data = data || null;
    const componentPortal = new ComponentPortal(component);
    this.contentChange$.next(componentPortal);
    this.toggle(true);
  }

  public close(data?: any): void {
    this.formChangedValue = false;
    this.contentChange$.next(null);
    this.afterClose$.next(data);
    this.toggle(false, data);
  }

  public toggle(open?: boolean, data?: any): void {
    this.isOpen = open !== undefined ? open : this.isOpen;
    this.toggle$.next({ open: this.isOpen, data });
  }

  public enableButtons(loading?: boolean): void {
    this.toggleDisabledButton$.next(false);

    if (loading !== undefined) {
      this.loading$.next(loading);
    }
  }

  public disableButtons(loading?: boolean): void {
    this.toggleDisabledButton$.next(true);

    if (loading !== undefined) {
      this.loading$.next(loading);
    }
  }

  public setDisabledButtons(disabled: boolean): void {
    this.toggleDisabledButton$.next(disabled);
  }

  public setDrawerConfiguration(config: IDrawerConfig): void {
    this.drawerConfig$.next(config);
  }

  public get isDrawerOpen(): boolean {
    return this.isOpen;
  }

  public startLoading() {
    this.loading$.next(true);
  }

  public stopLoading() {
    this.loading$.next(false);
  }

  allowClickHandler(label) {
    // isolate any secondary button that contains a label present in this list
    return allowClickHandlerList.some((buttonLabel) => label.indexOf(buttonLabel) > -1);
  }

  private handleNavigationEvent(): void {
    this.dialogSubscription.add(this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(
      (event: NavigationStart) => {
        if (this.isDrawerOpen && event.url !== this.router.url) {
          if (!this.formChangedValue) {
            this.close();
          }

          if (this.formChangedValue) {
            this.targetRoute = event.url;
            this.router.navigateByUrl(this.router.url);
            this.openConfirmationDialog();
          }
        }
    }));
  }

  getConfirmationDialogRef(): MatDialogRef<ConfirmDialogComponent, any> {
    return this.dialog.open<ConfirmDialogComponent, DialogData>(ConfirmDialogComponent, {
      panelClass: 'cm-confirm-dialog',
      autoFocus: false,
      data: {
        title: 'drawer.unsaved-changes',
        message: this.translateService.instant('drawer.discard-changes-message'),
        primaryAction: {
          label: 'drawer.confirm-discard',
          color: BasicButtonColors.DANGER,
        },
        secondaryAction: {
          label: 'drawer.cancel',
          mode: BasicButtonModes.FLAT,
        },
      },
    });
  }

  private openConfirmationDialog(): void {
    const dialogRef = this.getConfirmationDialogRef();

    this.dialogSubscription.add(dialogRef
      .afterClosed()
      .subscribe((result) => {
        if (result == 'drawer.confirm-discard') {
          this.confirmNavigation();
        }
      }));
  }

  confirmNavigation() {
    this.close();
    this.router.navigateByUrl(this.targetRoute);
  }

  isFormModified(form: FormGroup, originalValues: any) {
    this.formChangedValue = !isEqual(form.value, originalValues);
  }

  ngOnDestroy(): void {
    this.dialogSubscription.unsubscribe();
  }
}
